import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Form, Table, Modal, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPlus, faHistory } from '@fortawesome/free-solid-svg-icons';
import CurrencyFormat from 'react-currency-format';
import moment from 'moment';
import { toast } from 'react-toastify';
import axios from 'axios';
import getApiUrl from '../../../config/api';

const EmployeeBaseRate = ({ employee }) => {
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    
    // State
    const [baseRates, setBaseRates] = useState([]);
    const [currentRate, setCurrentRate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        monthly_base_rate: '',
        notes: ''
    });
    const [formErrors, setFormErrors] = useState({});

    // Load base rates on component mount
    useEffect(() => {
        if (employee?.firm_id && employee?.id) {
            loadBaseRates();
        }
    }, [employee?.firm_id, employee?.id]);

    const loadBaseRates = async () => {
        if (!employee?.firm_id || !employee?.id) return;
        
        setLoading(true);
        try {
            const response = await axios.get(`${getApiUrl()}/firm/${employee.firm_id}/employee-base-rates/employee/${employee.id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                const activeRate = response.data.data;
                setCurrentRate(activeRate);
                if (activeRate) {
                    setBaseRates([activeRate]);
                }
                
                if (activeRate) {
                    setFormData({
                        monthly_base_rate: activeRate.monthly_base_rate || 0,
                        notes: activeRate.notes || ''
                    });
                } else {
                    // Reset form data when no active rate
                    setFormData({
                        monthly_base_rate: '',
                        notes: ''
                    });
                }
            }
        } catch (error) {
            console.error('Error loading base rates:', error);
            toast.error('Bazaviy ish haqini yuklashda xatolik');
        } finally {
            setLoading(false);
        }
    };

    const loadHistory = async () => {
        if (!employee?.id || !employee?.firm_id) return;
        
        try {
            const response = await axios.get(`${getApiUrl()}/firm/${employee.firm_id}/employee-base-rates/employee/${employee.id}/history`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                setBaseRates(response.data.data);
            }
        } catch (error) {
            console.error('Error loading history:', error);
            toast.error('Tarixni yuklashda xatolik');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormErrors({});

        if (formData.monthly_base_rate === '' || formData.monthly_base_rate === null || formData.monthly_base_rate === undefined || isNaN(formData.monthly_base_rate)) {
            setFormErrors({ monthly_base_rate: 'Ish haqi miqdori kiritilishi shart' });
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${getApiUrl()}/firm/${employee.firm_id}/employee-base-rates/employee/${employee.id}`, {
                monthly_base_rate: parseFloat(formData.monthly_base_rate),
                effective_from: moment().format('YYYY-MM-DD'), // Auto-set to today
                notes: formData.notes
            }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.data.success) {
                toast.success('Bazaviy ish haqi muvaffaqiyatli yangilandi');
                setShowUpdateModal(false);
                loadBaseRates();
            }
        } catch (error) {
            console.error('Error updating base rate:', error);
            if (error.response?.data?.errors) {
                setFormErrors(error.response.data.errors);
            } else {
                toast.error('Ish haqini yangilashda xatolik');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleOpenUpdateModal = () => {
        // Reset form data when opening modal
        setFormData({
            monthly_base_rate: currentRate?.monthly_base_rate || '',
            notes: currentRate?.notes || ''
        });
        setFormErrors({});
        setShowUpdateModal(true);
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('uz-UZ').format(amount) + ' сум';
    };

    if (!employee) {
        return <div>Xodim ma'lumotlari yuklanmoqda...</div>;
    }

    return (
        <>
            <Card className="mb-3">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="card-title mb-0">Bazaviy ish haqi</h5>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleOpenUpdateModal}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faEdit} className="me-1" />
                                Yangilash
                            </Button>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => {
                                    setShowHistoryModal(true);
                                    loadHistory();
                                }}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faHistory} className="me-1" />
                                Tarix
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-3">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Yuklanmoqda...</span>
                            </div>
                        </div>
                    ) : currentRate ? (
                        <div className="row">
                            <div className="col-md-6">
                                <div className="d-flex align-items-center mb-2">
                                    <strong className="me-2">Oylik bazaviy ish haqi:</strong>
                                    <span className="text-success fs-5 fw-bold">
                                        {formatCurrency(currentRate.monthly_base_rate)}
                                    </span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <strong className="me-2">Amal qilish sanasi:</strong>
                                    <span>{moment(currentRate.effective_from).format('DD.MM.YYYY')}</span>
                                </div>
                                <div className="d-flex align-items-center mb-2">
                                    <strong className="me-2">Turi:</strong>
                                    <span className="badge bg-primary">Oylik</span>
                                </div>
                            </div>
                            <div className="col-md-6">
                                {currentRate.notes && (
                                    <div className="mb-2">
                                        <strong>Izoh:</strong>
                                        <p className="text-muted mt-1">{currentRate.notes}</p>
                                    </div>
                                )}
                                <div className="text-muted small">
                                    Yaratilgan: {moment(currentRate.created_at).format('DD.MM.YYYY HH:mm')}
                                </div>
                                {currentRate.updated_at !== currentRate.created_at && (
                                    <div className="text-muted small">
                                        Yangilangan: {moment(currentRate.updated_at).format('DD.MM.YYYY HH:mm')}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <Alert variant="warning">
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Hozircha bazaviy ish haqi belgilanmagan. Yangi ish haqi belgilash uchun "Yangilash" tugmasini bosing.
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            {/* Update Base Rate Modal */}
            <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {currentRate ? 'Bazaviy ish haqini yangilash' : 'Yangi bazaviy ish haqi belgilash'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-md-12">
                                <Form.Group className="mb-3">
                                    <Form.Label>Oylik bazaviy ish haqi (сум) *</Form.Label>
                                    <CurrencyFormat
                                        className={`form-control ${formErrors.monthly_base_rate ? 'is-invalid' : ''}`}
                                        value={isNaN(formData.monthly_base_rate) ? '' : formData.monthly_base_rate}
                                        onValueChange={(values) => {
                                            handleInputChange('monthly_base_rate', values.floatValue !== undefined ? values.floatValue : '');
                                        }}
                                        thousandSeparator={true}
                                        placeholder="0"
                                        suffix=" сум"
                                    />
                                    {formErrors.monthly_base_rate && (
                                        <div className="invalid-feedback">
                                            {formErrors.monthly_base_rate}
                                        </div>
                                    )}
                                    <Form.Text className="text-muted">
                                        Amal qilish sanasi: <strong>{moment().format('DD.MM.YYYY')}</strong> (bugun)
                                    </Form.Text>
                                </Form.Group>
                            </div>
                        </div>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Izoh</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                placeholder="Ish haqi o'zgarishining sababi..."
                            />
                        </Form.Group>

                        {currentRate && (
                            <Alert variant="info">
                                <strong>Hozirgi ish haqi:</strong> {formatCurrency(currentRate.monthly_base_rate)}
                                <br />
                                <small className="text-muted">
                                    Yangi ish haqi bugundan ({moment().format('DD.MM.YYYY')}) boshlab amal qiladi
                                </small>
                            </Alert>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowUpdateModal(false)}>
                            Bekor qilish
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? 'Saqlanmoqda...' : 'Saqlash'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* History Modal */}
            <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Ish haqi tarixi - {employee?.first_name} {employee?.last_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {baseRates.length > 0 ? (
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Oylik ish haqi</th>
                                    <th>Amal qilish sanasi</th>
                                    <th>Tugash sanasi</th>
                                    <th>Holat</th>
                                    <th>Izoh</th>
                                    <th>Yaratilgan</th>
                                </tr>
                            </thead>
                            <tbody>
                                {baseRates.map((rate, index) => (
                                    <tr key={rate.id} className={rate.is_active ? 'table-success' : ''}>
                                        <td>
                                            <strong>{formatCurrency(rate.monthly_base_rate)}</strong>
                                        </td>
                                        <td>{moment(rate.effective_from).format('DD.MM.YYYY')}</td>
                                        <td>
                                            {rate.effective_until 
                                                ? moment(rate.effective_until).format('DD.MM.YYYY')
                                                : <span className="text-muted">Hozir</span>
                                            }
                                        </td>
                                        <td>
                                            {rate.is_active ? (
                                                <span className="badge bg-success">Faol</span>
                                            ) : (
                                                <span className="badge bg-secondary">Yaroqsiz</span>
                                            )}
                                        </td>
                                        <td>{rate.notes || <span className="text-muted">-</span>}</td>
                                        <td>{moment(rate.created_at).format('DD.MM.YYYY HH:mm')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <Alert variant="info">
                            Hozircha ish haqi tarixi mavjud emas.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowHistoryModal(false)}>
                        Yopish
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EmployeeBaseRate;
